/**
 * Crew Message Detail Page
 *
 * Shows full message content with ability to reply.
 * Auto-marks message as read when opened.
 */

import { useState, useEffect, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getMyMessage,
  markMessageAsRead,
  replyToMessage,
  type InboxMessage,
  type MessageType,
} from '../../../api/messages';
import { getErrorMessage } from '../../../api/client';

const MESSAGE_TYPE_CONFIG: Record<MessageType, { label: string; className: string }> = {
  BROADCAST: { label: 'Direct Message', className: 'bg-blue-100 text-blue-800' },
  ASSIGNMENT: { label: 'Assignment', className: 'bg-purple-100 text-purple-800' },
  CREW_REPLY: { label: 'Crew Reply', className: 'bg-green-100 text-green-800' },
  EVENT_PUBLISHED: { label: 'New Event', className: 'bg-teal-100 text-teal-800' },
  EVENT_UPDATED: { label: 'Event Update', className: 'bg-amber-100 text-amber-800' },
};

export default function MessageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [message, setMessage] = useState<InboxMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reply state
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySuccess, setReplySuccess] = useState(false);

  const loadMessage = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { message } = await getMyMessage(id);
      setMessage(message);

      // Auto-mark as read if unread
      if (!message.readAt) {
        await markMessageAsRead(id);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessage();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!id || !replyBody.trim()) return;

    setSubmittingReply(true);
    setReplyError(null);

    try {
      await replyToMessage(id, { body: replyBody.trim() });
      setReplySuccess(true);
      setReplyBody('');
      setShowReply(false);
      // Reload to show the new reply in the thread
      await loadMessage();
      // Hide success after 3 seconds
      setTimeout(() => setReplySuccess(false), 3000);
    } catch (err) {
      setReplyError(getErrorMessage(err));
    } finally {
      setSubmittingReply(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error || 'Message not found'}</div>
        <Link to="/crew/messages" className="text-primary-600 hover:text-primary-500">
          Back to Inbox
        </Link>
      </div>
    );
  }

  const typeConfig = MESSAGE_TYPE_CONFIG[message.messageType];

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Link
        to="/crew/messages"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Inbox
      </Link>

      {/* Success message */}
      {replySuccess && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          Your reply has been sent and is visible to organization admins.
        </div>
      )}

      {/* Message card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h1 className="text-lg font-semibold text-gray-900">
              {message.subject}
            </h1>
            <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium ${typeConfig.className}`}>
              {typeConfig.label}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            <p>
              From: <span className="text-gray-700">{message.sentBy.firstName} {message.sentBy.lastName}</span>
            </p>
            <p>
              Organization: <span className="text-gray-700">{message.organization.name}</span>
            </p>
            <p>
              Sent: <span className="text-gray-700">{formatDate(message.sentAt)}</span>
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          <p className="text-gray-700 whitespace-pre-wrap">{message.body}</p>
        </div>

        {/* Channels */}
        <div className="px-4 pb-4">
          <div className="flex gap-2">
            <span className="text-xs text-gray-500">Sent via:</span>
            {message.channels.includes('EMAIL') && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                Email
              </span>
            )}
            {message.channels.includes('SMS') && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                SMS
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Replies thread */}
      {message.replies && message.replies.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">
            Replies ({message.replies.length})
          </h2>
          <div className="space-y-3">
            {message.replies.map((reply) => {
              const isCrewReply = reply.messageType === 'CREW_REPLY';
              return (
                <div
                  key={reply.id}
                  className={`border border-hairline border-l-[3px] bg-surface-panel p-3 ${isCrewReply ? 'border-l-preview' : 'border-l-rack'}`}
                >
                  <div className="mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center text-xs font-semibold ${isCrewReply ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {reply.sentBy.firstName.charAt(0)}
                        {reply.sentBy.lastName.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {reply.sentBy.firstName} {reply.sentBy.lastName}
                      </span>
                      <span className={`px-1.5 py-0.5 text-xs font-medium ${isCrewReply ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {isCrewReply ? 'Crew' : 'Admin'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(reply.sentAt)}
                    </p>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{reply.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reply section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {!showReply ? (
          <button
            onClick={() => setShowReply(true)}
            className="w-full py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Reply to this message
          </button>
        ) : (
          <form onSubmit={handleReply}>
            <div className="mb-3">
              <label htmlFor="reply" className="block text-sm font-medium text-gray-700 mb-1">
                Your Reply
              </label>
              <textarea
                id="reply"
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={4}
                placeholder="Type your reply..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your reply will be visible to organization admins in the app.
              </p>
            </div>

            {replyError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                {replyError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowReply(false);
                  setReplyBody('');
                  setReplyError(null);
                }}
                disabled={submittingReply}
                className="flex-1 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submittingReply || !replyBody.trim()}
                className="flex-1 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {submittingReply ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
